import { defineNuxtPlugin } from '#app';
import { fetchOneEntry } from '@builder.io/sdk-vue';
import { useBuilderComponents } from '~/modules/builder-components/runtime/composables/builder-components';
// resolveComponents,
// loadAllComponents,
// getComponentByFriendlyName,

const resolveComponents = async (friendlyNames: string[], builderComponents: any) => {
  return {};
}

const getComponentByFriendlyName = async (friendlyName: string) => {}

type Input = {
  name: string;
  type: string;
  defaultValue?: any;
  [key: string]: any; // Allow additional properties like min, max, step
};

function extractComponentNames(obj: any): string[] {
  const names: string[] = [];
  if (Array.isArray(obj)) {
    for (const item of obj) names.push(...extractComponentNames(item));
  } else if (obj && typeof obj === 'object') {
    // Extract friendly names from Builder.io component structure
    if (obj.component?.name) names.push(obj.component.name);
    for (const value of Object.values(obj)) {
      names.push(...extractComponentNames(value));
    }
  }
  return names;
}

function inferType(type: any): string {
  if (type === Boolean) return 'boolean';
  if (type === Number) return 'number';
  if (type === Object) return 'object';
  if (type === Array) return 'list';
  return 'string';
}

function extractInputsFromProps(props: any): Input[] {
  if (!props) return [];
  if (Array.isArray(props)) {
    return props.map((name: string) => ({ name, type: 'string' }));
  }
  const inputs: Input[] = [];
  for (const [name, options] of Object.entries(props)) {
    const opt = typeof options === 'object' && options !== null ? options : { type: options };
    inputs.push({
      name,
      type: inferType((opt as any).type),
      defaultValue: (opt as any).default,
    });
  }
  return inputs;
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      async fetchBuilderPage(model: string, apiKey: string, urlPath: string, isEditing: boolean, builderComponents: any) {
        const content = await fetchOneEntry({
          model,
          apiKey,
          userAttributes: { urlPath },
        });
        
        // Handle case where content is null/undefined or has no data
        if (!content || !content.data) {
          return { 
            content: null, 
            components: [] 
          };
        }

        // Handle case where content.data exists but has no blocks
        if (!content.data.blocks || !Array.isArray(content.data.blocks) || content.data.blocks.length === 0) {
          return { 
            content: {
              ...content,
              data: {
                ...content.data,
                blocks: []
              }
            }, 
            components: [] 
          };
        }

        // Extract friendly names from the content
        const friendlyNames = extractComponentNames(content.data);
        //console.log(builderComponents);
        // Load components by friendly name
        const registry = isEditing
          ? builderComponents
          : await resolveComponents(friendlyNames, builderComponents);

        //console.log(`Registry loaded: ${Object.keys(registry).length} components`);
        //console.log('Component names:', Object.keys(registry));
        
        let components: any[] = [];
        
        try {
          components = await Promise.all(
            Object.entries(registry).map(async ([friendlyName, loader]) => {
              try {
                const mod = await loader();
                const componentInfo = await getComponentByFriendlyName(friendlyName);
                
                if (!componentInfo || !componentInfo.metadata) {
                  console.warn(`No metadata found for component: ${friendlyName}`);
                  return null;
                }

                const metadata = componentInfo.metadata;
                
                // Use inputs from defineBuilderComponent if available, otherwise infer from props
                const inputs = metadata.inputs || extractInputsFromProps(mod.default?.props);

                return {
                  name: friendlyName, // Use friendly name as the component name
                  canHaveChildren: metadata.canHaveChildren ?? true,
                  inputs,
                  // Include additional metadata if available
                  description: metadata.description,
                  keywords: metadata.keywords,
                  category: metadata.category,
                };
              } catch (error) {
                console.warn(`Error processing component ${friendlyName}:`, error);
                return null;
              }
            })
          );

          // Filter out null entries
          components = components.filter(Boolean);
          
          //console.log(`Successfully processed ${components.length} components:`);
          components.forEach(comp => {
            //console.log(`- ${comp.name}: ${comp.inputs.length} inputs`);
          });
          
        } catch (error) {
          console.error('Error loading components:', error);
        }

        // Only return serializable data - registry will be recreated on client side
        return { content, components };
      },
    },
  };
});
